import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get, findIndex } from 'lodash';
import cns from 'classnames';
import { Row, Collapse } from 'react-bootstrap';

import { SECTION_ITEM_TYPES, LEARNING_ASSET_TYPES } from '../../../../../shared/Constants';

// Components
import SectionItemDefaultFields from './SectionItemDefaultFields';
import SectionItemSimpleDefaultFields from './SectionItemSimpleDefaultFields';
import SurveySectionItem from './SurveySectionItem';
import ShortEssaySectionItem from './ShortEssaySectionItem';
import LikertSectionItem from './LikertSectionItem';
import ThisOrThatPermutationSectionItem from './ThisOrThat/ThisOrThatSectionItem';
import ThisOrThatPermutationResultSectionItem from './ThisOrThatPermutationResultSectionItem';
import ThisOrThatPermutationResultChartSectionItem from './ThisOrThatPermutationResultChartSectionItem';
import ChooseOneSectionItem from './ChooseOneSectionItem';
import EmailSectionItem from './EmailSectionItem';
import OnscreenSectionItem from './OnscreenSectionItem';
import ChooseManySectionItem from './ChooseManySectionItem';
import RankingSectionItem from './RankingSectionItem';
import DynamicRichTextSectionItem from './DynamicRichTextSectionItem/DynamicRichTextSectionItem';
import UserInviteSectionItem from './UserInviteSectionItem';
import ChooseOneResultsSectionItem from './ChooseOneResultsSectionItem';
import AggregateShortEssaySectionItem from './AggregateShortEssaySectionItem';
import FileUploadSectionItem from './FileUploadSectionItem';
import ChooseOneQuizSectionItem from './ChooseOneQuizSectionItem';
import QuizResultsSectionItem from './QuizResultsSectionItem';
import ShowPreviousAnswersSectionItem from './ShowPreviousAnswersSectionItem';
import FeedbackSectionItem from './FeedbackSectionItem';
import FeedbackWrapUpSectionItem from './FeedbackWrapUpSectionItem';
import FeedbackInviteSectionItem from './FeedbackInviteSectionItem';
import WrapUpSectionItem from './WrapUpSectionItem';
import ShowPreviousAndChooseSectionItem from './ShowPreviousAndChooseSectionItem';
import ReviewPreviousActivitiesSectionItem from './ReviewPreviousActivitiesSectionItem';
import ChooseOnePathSectionItem from './ChooseOnePathSectionItem';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../shared/components/VerbButton';

const SECTION_ITEMS_KEY = 'sectionItems';
const SECTIONS_KEY = 'sections';

class SectionItemFormSection extends Component {
    static propTypes = {
        currentValues: PropTypes.object,
        type: PropTypes.oneOf([
            LEARNING_ASSET_TYPES.TRACK,
            LEARNING_ASSET_TYPES.ACTIVITY,
            LEARNING_ASSET_TYPES.ARTICLE,
            LEARNING_ASSET_TYPES.SINGLE_PAGE_ACTIVITY,
            LEARNING_ASSET_TYPES.VIDEO,
            LEARNING_ASSET_TYPES.IMPACT_PROJECT,
            LEARNING_ASSET_TYPES.ASSESSMENT,
            LEARNING_ASSET_TYPES.PRE_ASSESSMENT,
            LEARNING_ASSET_TYPES.POST_ASSESSMENT,
            LEARNING_ASSET_TYPES.FEEDBACK,
            LEARNING_ASSET_TYPES.WELCOME,
            LEARNING_ASSET_TYPES.MOBILE,
            LEARNING_ASSET_TYPES.PRACTICAL_APPLICATION,
            LEARNING_ASSET_TYPES.REFLECTION
        ]),
        selectedSectionId: PropTypes.string,
        selectedSectionItemId: PropTypes.string,
        surveys: PropTypes.array,
        swapItem: PropTypes.func.isRequired,
        onDeleteSectionItem: PropTypes.func.isRequired,
        changeFormValue: PropTypes.func.isRequired,
        assetLinkOptions: PropTypes.array,
        globalSectionItems: PropTypes.array,
        getLearningAsset: PropTypes.func,
        globalAddSuccessMessage: PropTypes.string,
        globalSubmitSucceeded: PropTypes.bool,
        onGlobalSaveSectionItem: PropTypes.func,
        hideForContentAuthor: PropTypes.bool,
        collectionsByLearningAssetId: PropTypes.object
    };

    constructor(props) {
        super(props);
        this.state = {
            open: true
        };
    }

    /**
     * Private Methods
     */

    /**
     *
     * @param {string} property
     * @param {*} [defaultValue='']
     * @return {*}
     * @private
     */
    _getFormValue = (property, defaultValue = '') => get(this.props.currentValues, `${property}`, defaultValue);

    /**
     * Iterates through all sectionItems to generate a list of potential references for A\B Permutation Results.
     * @returns {Array}
     * @private
     */
    _generateABPermutationDataSource = () =>
        this._generateQuestionDataSource(SECTION_ITEM_TYPES.THIS_OR_THAT_PERMUTATION_CONTENT);

    /**
     * Iterates through all sectionItems to generate a list of potential references for Short Essay Results.
     * @returns {Array}
     * @private
     */
    _generateShortEssaySource = () => this._generateQuestionDataSource(SECTION_ITEM_TYPES.SHORT_ESSAY_CONTENT);

    /**
     * Iterates through all sectionItems to generate a list of potential references for Choose One Results
     * @returns {Array}
     * @private
     */
    _generateChooseOneDataSource = () => this._generateQuestionDataSource(SECTION_ITEM_TYPES.CHOOSE_ONE_CONTENT);

    populateGlobalSectionItemData = (currentSection, globalSectionItemData = {}) => {
        const { changeFormValue } = this.props;
        changeFormValue({ field: `${currentSection}.type`, value: globalSectionItemData.type });
        changeFormValue({ field: `${currentSection}.name`, value: globalSectionItemData.name });
        changeFormValue({ field: `${currentSection}.content`, value: globalSectionItemData.content });
    };

    /**
     *
     * @param {string} type - SECTION_ITEM_TYPES string to lookup items
     * @return {Array}
     * @private
     */
    _generateQuestionDataSource = (type) => {
        const { currentValues } = this.props;
        const results = [];

        const sections = get(currentValues, 'sections', []) || [];

        sections.forEach((section) => {
            const sectionItems = get(section, 'sectionItems', []) || [];

            sectionItems.forEach((sectionItem) => {
                const formattedSectionItemType = (get(sectionItem, 'type', '') || '').toLocaleUpperCase();
                if (formattedSectionItemType === type) {
                    results.push(sectionItem);
                }
            });
        });

        return results;
    };

    render() {
        const {
            selectedSectionId,
            selectedSectionItemId,
            surveys,
            swapItem,
            onDeleteSectionItem,
            onGlobalSaveSectionItem,
            changeFormValue,
            type,
            assetLinkOptions,
            globalSectionItems,
            currentValues,
            getLearningAsset,
            globalAddSuccessMessage,
            globalSubmitSucceeded,
            hideForContentAuthor,
            collectionsByLearningAssetId
        } = this.props;
        const sectionIndex = findIndex(this._getFormValue(SECTIONS_KEY), { id: selectedSectionId });
        const sectionItems = this._getFormValue(`${SECTIONS_KEY}[${sectionIndex}].${SECTION_ITEMS_KEY}`);
        const SECTION_ITEMS_LENGTH = sectionItems.length;
        const index = findIndex(sectionItems, { id: selectedSectionItemId });

        const localSectionItemMemberReference = `${SECTIONS_KEY}[${sectionIndex}].${SECTION_ITEMS_KEY}[${index}]`;
        let sectionItemName = this._getFormValue(`${localSectionItemMemberReference}.name`);
        const currentSectionItemFormValues =
            (currentValues && currentValues[SECTIONS_KEY][sectionIndex][SECTION_ITEMS_KEY][index]) || {};
        const isGlobalSectionItem = !!currentSectionItemFormValues.globalSectionItemId || false;
        const sectionItemType = this._getFormValue(`${localSectionItemMemberReference}.type`);
        const contentValues = this._getFormValue(`${localSectionItemMemberReference}.content`, {});
        const computedSectionItemType = sectionItemType.toLocaleUpperCase();
        let childComponent;
        const defaultSectionItemProps = {
            sectionItemId: selectedSectionItemId,
            memberReference: localSectionItemMemberReference,
            contentValues,
            getFormValue: this._getFormValue,
            assetLinkOptions,
            isGlobalSectionItem
        };

        if (!sectionItemType) {
            childComponent = null;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.SURVEY_CONTENT) {
            defaultSectionItemProps.surveys = surveys;
            childComponent = <SurveySectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.SHORT_ESSAY_CONTENT) {
            childComponent = <ShortEssaySectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.LIKERT_CONTENT) {
            defaultSectionItemProps.changeFormValue = changeFormValue;
            childComponent = <LikertSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.THIS_OR_THAT_PERMUTATION_CONTENT) {
            childComponent = <ThisOrThatPermutationSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.THIS_OR_THAT_RESULTS_CONTENT) {
            const permutationQuestionDataSource = this._generateABPermutationDataSource();
            childComponent = (
                <ThisOrThatPermutationResultSectionItem
                    {...defaultSectionItemProps}
                    permutationQuestionDataSource={permutationQuestionDataSource}
                />
            );
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.THIS_OR_THAT_RESULTS_CHART) {
            const permutationQuestionDataSource = this._generateABPermutationDataSource();
            childComponent = (
                <ThisOrThatPermutationResultChartSectionItem
                    {...defaultSectionItemProps}
                    permutationQuestionDataSource={permutationQuestionDataSource}
                />
            );
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.CHOOSE_ONE_CONTENT) {
            childComponent = <ChooseOneSectionItem changeFormValue={changeFormValue} {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.CHOOSE_MANY_CONTENT) {
            childComponent = <ChooseManySectionItem changeFormValue={changeFormValue} {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.CHOOSE_ONE_PATH_CONTENT) {
            childComponent = (
                <ChooseOnePathSectionItem
                    getFormValue={this._getFormValue}
                    changeFormValue={changeFormValue}
                    {...defaultSectionItemProps}
                />
            );
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.EMAIL_NUDGE) {
            childComponent = <EmailSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.ONSCREEN_NUDGE) {
            childComponent = <OnscreenSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.RANKING_CONTENT) {
            childComponent = <RankingSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.RICH_TEXT_CONTENT) {
            if (type === LEARNING_ASSET_TYPES.VIDEO) {
                defaultSectionItemProps.isVideo = true;
            }

            defaultSectionItemProps.changeFormValue = changeFormValue;

            childComponent = (
                <DynamicRichTextSectionItem convertToDynamicRichText={true} {...defaultSectionItemProps} />
            );
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.USER_INVITE_CONTENT) {
            childComponent = <UserInviteSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.CHOOSE_ONE_RESULTS_CONTENT) {
            const chooseOneDataSource = this._generateChooseOneDataSource();

            childComponent = (
                <ChooseOneResultsSectionItem
                    chooseOneQuestionDataSource={chooseOneDataSource}
                    {...defaultSectionItemProps}
                />
            );
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.SHORT_ESSAY_RESULT) {
            const shortEssayDataSource = this._generateShortEssaySource();
            childComponent = (
                <AggregateShortEssaySectionItem
                    {...defaultSectionItemProps}
                    shortEssayDataSource={shortEssayDataSource}
                />
            );
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.FILE_UPLOAD_CONTENT) {
            childComponent = <FileUploadSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.DYNAMIC_RICH_TEXT_CONTENT) {
            defaultSectionItemProps.changeFormValue = changeFormValue;
            childComponent = <DynamicRichTextSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.CHOOSE_ONE_QUIZ) {
            defaultSectionItemProps.changeFormValue = changeFormValue;

            childComponent = <ChooseOneQuizSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.QUIZ_RESULTS) {
            childComponent = <QuizResultsSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.SHOW_PREVIOUS_ANSWERS) {
            childComponent = (
                <ShowPreviousAnswersSectionItem
                    {...defaultSectionItemProps}
                    currentSectionItemFormValues={currentSectionItemFormValues}
                    likertQuestionDataSource={this._generateQuestionDataSource(SECTION_ITEM_TYPES.LIKERT_CONTENT)}
                    chooseOneQuestionDataSource={this._generateQuestionDataSource(
                        SECTION_ITEM_TYPES.CHOOSE_ONE_CONTENT
                    )}
                    chooseManyQuestionDataSource={this._generateQuestionDataSource(
                        SECTION_ITEM_TYPES.CHOOSE_MANY_CONTENT
                    )}
                    shortEssayQuestionDataSource={this._generateQuestionDataSource(
                        SECTION_ITEM_TYPES.SHORT_ESSAY_CONTENT
                    )}
                    showPreviousAndChooseDataSource={this._generateQuestionDataSource(
                        SECTION_ITEM_TYPES.SHOW_PREVIOUS_ANSWERS_CHOOSE_ONE
                    )}
                />
            );
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.SHOW_PREVIOUS_ANSWERS_CHOOSE_ONE) {
            childComponent = (
                <ShowPreviousAndChooseSectionItem
                    {...defaultSectionItemProps}
                    currentSectionItemFormValues={currentSectionItemFormValues}
                    likertQuestionDataSource={this._generateQuestionDataSource(SECTION_ITEM_TYPES.LIKERT_CONTENT)}
                    chooseOneQuestionDataSource={this._generateQuestionDataSource(
                        SECTION_ITEM_TYPES.CHOOSE_ONE_CONTENT
                    )}
                    chooseManyQuestionDataSource={this._generateQuestionDataSource(
                        SECTION_ITEM_TYPES.CHOOSE_MANY_CONTENT
                    )}
                    shortEssayQuestionDataSource={this._generateQuestionDataSource(
                        SECTION_ITEM_TYPES.SHORT_ESSAY_CONTENT
                    )}
                    showPreviousAndChooseDataSource={this._generateQuestionDataSource(
                        SECTION_ITEM_TYPES.SHOW_PREVIOUS_ANSWERS_CHOOSE_ONE
                    )}
                />
            );
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.FEEDBACK_CONTENT) {
            childComponent = <FeedbackSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.FEEDBACK_WRAPUP) {
            childComponent = <FeedbackWrapUpSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.FEEDBACK_INVITE) {
            childComponent = <FeedbackInviteSectionItem {...defaultSectionItemProps} />;
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.REVIEW_PREV_ACTIVITIES) {
            childComponent = (
                <ReviewPreviousActivitiesSectionItem
                    {...defaultSectionItemProps}
                    getLearningAsset={getLearningAsset}
                    changeFormValue={changeFormValue}
                    collectionsByLearningAssetId={collectionsByLearningAssetId}
                />
            );
        } else if (computedSectionItemType === SECTION_ITEM_TYPES.WRAP_UP) {
            sectionItemName = 'Wrap Up';
            childComponent = <WrapUpSectionItem {...defaultSectionItemProps} />;
        }
        const canSectionItemMoveUp = index - 1 >= 0;
        const canSectionItemMoveDown = index + 1 < SECTION_ITEMS_LENGTH;

        let defaultFieldsSection = null;
        if (
            this.props.type === LEARNING_ASSET_TYPES.TRACK ||
            this.props.type === LEARNING_ASSET_TYPES.IMPACT_PROJECT ||
            this.props.type === LEARNING_ASSET_TYPES.ACTIVITY ||
            this.props.type === LEARNING_ASSET_TYPES.ASSESSMENT ||
            this.props.type === LEARNING_ASSET_TYPES.PRE_ASSESSMENT ||
            this.props.type === LEARNING_ASSET_TYPES.POST_ASSESSMENT ||
            this.props.type === LEARNING_ASSET_TYPES.FEEDBACK ||
            this.props.type === LEARNING_ASSET_TYPES.VIDEO ||
            this.props.type === LEARNING_ASSET_TYPES.ARTICLE ||
            this.props.type === LEARNING_ASSET_TYPES.WELCOME ||
            this.props.type === LEARNING_ASSET_TYPES.SINGLE_PAGE_ACTIVITY ||
            this.props.type === LEARNING_ASSET_TYPES.MOBILE ||
            this.props.type === LEARNING_ASSET_TYPES.PRACTICAL_APPLICATION ||
            this.props.type === LEARNING_ASSET_TYPES.REFLECTION
        ) {
            defaultFieldsSection = (
                <SectionItemDefaultFields
                    className={cns({ hidden: !this.state.open })}
                    getFormValue={this._getFormValue}
                    changeFormValue={this.props.changeFormValue}
                    selectedSectionId={selectedSectionId}
                    localSectionItemMemberReference={localSectionItemMemberReference}
                    sectionItemType={sectionItemType}
                    type={this.props.type}
                    hideForContentAuthor={hideForContentAuthor}
                    globalSectionItems={globalSectionItems}
                    isGlobalSectionItem={isGlobalSectionItem}
                    globalAddSuccessMessage={globalAddSuccessMessage}
                    globalSubmitSucceeded={globalSubmitSucceeded}
                    populateGlobalSectionItemData={(globalSectionItemData) =>
                        this.populateGlobalSectionItemData(localSectionItemMemberReference, globalSectionItemData)
                    }
                    globalSectionItemId={currentSectionItemFormValues.globalSectionItemId}
                    isWrapUpSectionItem={computedSectionItemType === SECTION_ITEM_TYPES.WRAP_UP}
                />
            );
        } else {
            defaultFieldsSection = (
                <SectionItemSimpleDefaultFields
                    className={cns({ hidden: !this.state.open })}
                    getFormValue={this._getFormValue}
                    changeFormValue={this.props.changeFormValue}
                    selectedSectionId={selectedSectionId}
                    localSectionItemMemberReference={localSectionItemMemberReference}
                    sectionItemType={sectionItemType}
                    type={this.props.type}
                    globalSectionItems={globalSectionItems}
                    isGlobalSectionItem={isGlobalSectionItem}
                    populateGlobalSectionItemData={(globalSectionItemData) =>
                        this.populateGlobalSectionItemData(localSectionItemMemberReference, globalSectionItemData)
                    }
                />
            );
        }

        return (
            <div>
                <Row>
                    <div className={'col-md-9'}>
                        <h2 className={'sectionItemName'}>{sectionItemName || 'New Section Item'}</h2>
                    </div>
                    <div className={'col-md-3'} style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                            fontAwesomeIcon={'fa-trash'}
                            className={'sectionItemFormButton'}
                            style={{ minWidth: '40px', padding: '6px 8px', fontSize: '14px' }}
                            onClick={() => {
                                onDeleteSectionItem(
                                    sectionItemName,
                                    `${SECTIONS_KEY}[${sectionIndex}].${SECTION_ITEMS_KEY}`,
                                    index
                                );
                            }}
                        />
                        {!isGlobalSectionItem && onGlobalSaveSectionItem && !hideForContentAuthor && (
                            <VerbButton
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                                fontAwesomeIcon={'far fa-sign-in-alt'}
                                className={'sectionItemFormButton'}
                                style={{ minWidth: '40px', padding: '6px 8px', fontSize: '14px' }}
                                onClick={() => {
                                    onGlobalSaveSectionItem(
                                        localSectionItemMemberReference,
                                        currentSectionItemFormValues
                                    );
                                }}
                            />
                        )}
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                            fontAwesomeIcon={'fa-angle-down'}
                            className={'sectionItemFormButton'}
                            style={{ minWidth: '40px', padding: '6px 8px', fontSize: '14px' }}
                            disabled={!canSectionItemMoveDown}
                            onClick={() => {
                                swapItem(`${SECTIONS_KEY}[${sectionIndex}].${SECTION_ITEMS_KEY}`, index, index + 1);
                            }}
                        />
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                            fontAwesomeIcon={'fa-angle-up'}
                            className={'sectionItemFormButton'}
                            style={{ minWidth: '40px', padding: '6px 8px', fontSize: '14px' }}
                            disabled={!canSectionItemMoveUp}
                            onClick={() => {
                                swapItem(`${SECTIONS_KEY}[${sectionIndex}].${SECTION_ITEMS_KEY}`, index, index - 1);
                            }}
                        />
                    </div>
                </Row>
                <Row className={'trackSectionDefaults'}>
                    <Collapse in={this.state.open} unmountOnExit={true}>
                        {defaultFieldsSection}
                    </Collapse>
                </Row>
                <Row>{childComponent}</Row>
            </div>
        );
    }
}

export default SectionItemFormSection;
